import { useState, useEffect, useRef, useCallback } from 'react'
import { Search, X, Loader2 } from 'lucide-react'
import type { SchemaField } from '../../../../types/schede'
import { DB_LOOKUP_ENTITIES } from '../../../../types/schede'
import FieldWrapper from './FieldWrapper'
import api from '../../../../core/services/api'

interface DbLookupFieldProps {
  field: SchemaField
  value: any
  onChange: (value: any) => void
  readOnly?: boolean
  error?: string
}

interface LookupOption {
  value: string
  label: string
}

export default function DbLookupField({ field, value, onChange, readOnly, error }: DbLookupFieldProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [options, setOptions] = useState<LookupOption[]>([])
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  const entityType = field.db_lookup_entity || 'clienti'
  const entityLabel = DB_LOOKUP_ENTITIES[entityType] || entityType

  // Carica label per il valore corrente
  useEffect(() => {
    if (!value) {
      setSelectedLabel(null)
      return
    }
    // Cerca la label nella lista options corrente
    const found = options.find(o => o.value === value)
    if (found) {
      setSelectedLabel(found.label)
      return
    }
    // Non fare la chiamata API se non c'è token (pagine pubbliche)
    if (!localStorage.getItem('erp_token')) {
      setSelectedLabel(value.slice(0, 8) + '...')
      return
    }
    // Fetch singolo per recuperare la label
    api.get(`/v1/schede-dinamiche/lookup/${entityType}?search=&limit=200`)
      .then(res => {
        const match = (res.data as LookupOption[]).find(o => o.value === value)
        if (match) setSelectedLabel(match.label)
        else setSelectedLabel(value.slice(0, 8) + '...')
      })
      .catch(() => setSelectedLabel(value.slice(0, 8) + '...'))
  }, [value, entityType])

  const fetchOptions = useCallback(async (search: string) => {
    // Non cercare se non c'è token (pagine pubbliche)
    if (!localStorage.getItem('erp_token')) {
      setOptions([])
      return
    }
    setLoading(true)
    try {
      const res = await api.get(`/v1/schede-dinamiche/lookup/${entityType}`, {
        params: { search, limit: 30 },
      })
      setOptions(res.data)
    } catch {
      setOptions([])
    } finally {
      setLoading(false)
    }
  }, [entityType])

  // Debounced search
  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchOptions(term), 250)
  }, [fetchOptions])

  // Open dropdown
  const handleOpen = useCallback(() => {
    if (readOnly) return
    setIsOpen(true)
    setSearchTerm('')
    fetchOptions('')
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [readOnly, fetchOptions])

  // Select option
  const handleSelect = useCallback((opt: LookupOption) => {
    onChange(opt.value)
    setSelectedLabel(opt.label)
    setIsOpen(false)
    setSearchTerm('')
  }, [onChange])

  // Clear
  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
    setSelectedLabel(null)
  }, [onChange])

  // Click outside to close
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen])

  return (
    <FieldWrapper field={field} error={error}>
      <div ref={wrapperRef} className="relative">
        {/* Display / trigger */}
        {!isOpen ? (
          <div
            onClick={handleOpen}
            className="w-full flex items-center gap-2 px-2.5 py-1.5 text-sm border rounded cursor-pointer"
            style={{
              backgroundColor: 'var(--color-bg-input, var(--color-bg-card))',
              borderColor: 'var(--color-border)',
              color: value ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)',
              cursor: readOnly ? 'default' : 'pointer',
            }}
          >
            <Search className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--color-text-tertiary)' }} />
            <span className="flex-1 truncate">
              {selectedLabel || `Cerca in ${entityLabel}...`}
            </span>
            {value && !readOnly && (
              <button
                onClick={handleClear}
                className="p-0.5 rounded hover:opacity-70"
                style={{ color: 'var(--color-text-tertiary)' }}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ) : (
          /* Search input */
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={`Cerca in ${entityLabel}...`}
            className="w-full px-2.5 py-1.5 text-sm border rounded outline-none"
            style={{
              backgroundColor: 'var(--color-bg-input, var(--color-bg-card))',
              borderColor: 'var(--color-primary)',
              color: 'var(--color-text-primary)',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setIsOpen(false)
            }}
          />
        )}

        {/* Dropdown */}
        {isOpen && (
          <div
            className="absolute z-50 left-0 right-0 mt-1 max-h-48 overflow-y-auto rounded-lg border shadow-lg"
            style={{
              backgroundColor: 'var(--color-bg-card)',
              borderColor: 'var(--color-border)',
            }}
          >
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--color-primary)' }} />
              </div>
            ) : options.length === 0 ? (
              <div className="px-3 py-3 text-xs text-center" style={{ color: 'var(--color-text-tertiary)' }}>
                {searchTerm ? 'Nessun risultato' : 'Nessun elemento disponibile'}
              </div>
            ) : (
              options.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handleSelect(opt)}
                  className="w-full px-3 py-2 text-left text-sm hover:opacity-80 transition-colors"
                  style={{
                    backgroundColor: opt.value === value ? 'var(--color-primary-light, rgba(99,102,241,0.1))' : 'transparent',
                  }}
                >
                  {opt.label}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </FieldWrapper>
  )
}
